import { useCallback } from "react";
import { PlatformListToolbar } from "@/components/platform/PlatformListToolbar";
import { usePlatformList } from "@/components/platform/usePlatformList";
import { DataTableDateTime } from "@/components/data-table";
import { AdminAlert, AdminEmptyState, AdminPageHeader, AdminPill } from "@/components/shell/AdminPage";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchPlatformBrands, type PlatformBrandRow } from "@/lib/platform-api";

const DISCARDED_FILTER_OPTIONS = [
  { value: "", label: "全部" },
  { value: "false", label: "正常" },
  { value: "true", label: "已丢弃" },
] as const;

export default function PlatformBrandsPage() {
  const fetcher = useCallback(async ({ q, filter, page }: { q: string; filter: string; page: number }) => {
    const result = await fetchPlatformBrands({
      q: q || undefined,
      discarded: (filter as "true" | "false" | "") || "",
      page,
    });
    return { rows: result.brands, meta: result.meta };
  }, []);

  const { inputQ, setInputQ, filter, setFilter, page, setPage, rows, meta, loading, error, submitSearch } =
    usePlatformList<PlatformBrandRow>({ fetcher });

  return (
    <div className="w-full space-y-6">
      <AdminPageHeader title="平台品牌" description="查看全平台品牌（Brand）及所属账户与丢弃状态。" />
      <PlatformListToolbar
        searchPlaceholder="搜索品牌或账户名称…"
        search={inputQ}
        onSearchChange={setInputQ}
        onSearchSubmit={submitSearch}
        filterValue={filter}
        onFilterChange={setFilter}
        filterOptions={[...DISCARDED_FILTER_OPTIONS]}
        meta={meta}
        page={page}
        onPageChange={setPage}
        loading={loading}
      />
      {error ? <AdminAlert>{error}</AdminAlert> : null}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">ID</TableHead>
              <TableHead>品牌名称</TableHead>
              <TableHead>所属账户</TableHead>
              <TableHead>状态</TableHead>
              <TableHead>创建时间</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  加载中…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="p-0">
                  <AdminEmptyState>
                    <p className="font-medium text-foreground">暂无匹配品牌</p>
                    <p className="mt-2">试试调整搜索词或状态筛选条件。</p>
                  </AdminEmptyState>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((brand) => (
                <TableRow key={brand.id}>
                  <TableCell className="tabular-nums text-muted-foreground">{brand.id}</TableCell>
                  <TableCell className="font-medium">{brand.name}</TableCell>
                  <TableCell>{brand.account_name}</TableCell>
                  <TableCell>
                    <AdminPill tone={brand.discarded ? "warning" : "success"}>
                      {brand.discarded ? "已丢弃" : "正常"}
                    </AdminPill>
                  </TableCell>
                  <TableCell>
                    <DataTableDateTime value={brand.created_at} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
